import React from "react";
import { createPortal } from "react-dom";
import { Grid3x3, Calculator } from "lucide-react";
import { formatPrice } from "../../lib/matrix";

// Off-screen branded quote card (shares .share-* styles with ShareCard) rasterised for PNG / PDF.
export default function QuoteCard({ cardRef, cost, pricing, priceMode }) {
  const symbol = pricing?.symbol || "$";
  const date = new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const url = `${window.location.origin}${window.location.pathname}`;
  return createPortal(
    <div className="share-stage" aria-hidden="true">
      <div className="share-card quote" ref={cardRef} data-testid="quote-card">
        <div className="share-top">
          <div className="share-brand">
            <span className="share-mark">
              <Grid3x3 size={20} strokeWidth={2.4} />
            </span>
            <span>
              Microsoft <b>License Hub</b>
            </span>
          </div>
          <div className="share-meta">
            <span className="share-chip">{pricing?.currency || "USD"} · {pricing?.label || "United States"}</span>
            <span className="share-chip">{priceMode === "teams" ? "With Teams" : "No Teams"}</span>
            <span className="share-chip muted">{date}</span>
          </div>
        </div>

        <h1 className="share-title">
          <Calculator size={34} strokeWidth={2.2} />
          License cost estimate
        </h1>
        <p className="share-sub">
          {cost.seats.toLocaleString()} seats across {cost.lines.length} line{cost.lines.length === 1 ? "" : "s"} · list price, annual subscription
        </p>

        <table className="quote-table">
          <thead>
            <tr>
              <th>License</th>
              <th className="num">Unit / user / mo</th>
              <th className="num">Seats</th>
              <th className="num">Monthly</th>
              <th className="num">Annual</th>
            </tr>
          </thead>
          <tbody>
            {cost.lines.map((l) => (
              <tr key={l.key}>
                <td>
                  <span className={`share-part ${l.kind !== "plan" ? "extra" : ""}`}>{l.name}</span>
                  {l.kind !== "plan" && <span className="quote-kind">{l.kind === "suite" ? "suite add-on" : "add-on"}</span>}
                </td>
                <td className="num mono">{l.unit === null || l.unit === undefined ? "\u2014" : formatPrice(l.unit, symbol, l.estimated)}</td>
                <td className="num mono">{l.qty.toLocaleString()}</td>
                <td className="num mono">{l.monthly === null ? "\u2014" : formatPrice(l.monthly, symbol, l.estimated)}</td>
                <td className="num mono">{l.annual === null ? "\u2014" : formatPrice(l.annual, symbol, l.estimated)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>Total</td>
              <td />
              <td className="num mono">{cost.seats.toLocaleString()}</td>
              <td className="num mono total">{formatPrice(cost.monthly, symbol, cost.estimated)}</td>
              <td className="num mono total">{formatPrice(cost.annual, symbol, cost.estimated)}</td>
            </tr>
          </tfoot>
        </table>

        <div className="share-foot">
          <span>
            {pricing?.note}. Annual = monthly x 12. {cost.estimated ? "\u2248 = estimated from US list price. " : ""}
            {cost.unpriced.length ? `${cost.unpriced.length} line(s) without a list price are excluded from totals. ` : ""}
            List prices exclude taxes and partner discounts - verify with Microsoft before purchasing.
          </span>
          <span className="share-url">{url}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
